import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError, rupiah } from "./lib/format.js";

const emptyForm = {
  name: "",
  cost_type: "PER_TREATMENT",
  amount: "",
  division: "ALL",
  status: "ACTIVE",
  notes: "",
};

const costTypes = [
  { value: "PER_TREATMENT", label: "Per tindakan" },
  { value: "PERCENTAGE", label: "Persentase tarif" },
  { value: "ALLOCATED", label: "Alokasi operasional" },
  { value: "MANUAL", label: "Manual" },
];

const divisions = [
  { value: "ALL", label: "Semua divisi" },
  { value: "WELLNESS", label: "Wellness / TCM" },
  { value: "ESTETIK", label: "Estetik" },
  { value: "DENTAL", label: "Dental" },
  { value: "DOKTER_UMUM", label: "Dokter Umum" },
  { value: "FARMASI", label: "Farmasi" },
  { value: "FISIOTERAPI", label: "Fisioterapi" },
  { value: "SALON", label: "Salon" },
];

function costTypeLabel(value) {
  return costTypes.find((item) => item.value === value)?.label || value || "-";
}

function divisionLabel(value) {
  return divisions.find((item) => item.value === value)?.label || value || "Semua divisi";
}

function formatAmount(row) {
  if (row.cost_type === "PERCENTAGE") return `${Number(row.amount || 0)}%`;
  return rupiah.format(Number(row.amount || 0));
}

export function MasterBiayaPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function fetchRows() {
    setLoading(true);
    setError("");
    try {
      const { data, error: fetchError } = await supabase
        .from("service_cost_catalog")
        .select("*")
        .eq("clinic_id", CLINIC_ID)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });
      if (fetchError) throw fetchError;
      setRows(data || []);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchRows();
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      return [row.name, row.division, row.notes].some((value) =>
        String(value || "").toLowerCase().includes(term)
      );
    });
  }, [rows, search]);

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
    setFormOpen(false);
    setError("");
  }

  function startEdit(row) {
    setEditingId(row.id);
    setFormOpen(true);
    setForm({
      name: row.name || "",
      cost_type: row.cost_type || "PER_TREATMENT",
      amount: String(row.amount ?? ""),
      division: row.division || "ALL",
      status: row.status || "ACTIVE",
      notes: row.notes || "",
    });
    setError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Nama pos biaya wajib diisi.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        clinic_id: CLINIC_ID,
        name: form.name.trim(),
        cost_type: form.cost_type || "PER_TREATMENT",
        amount: Number(form.amount) || 0,
        division: form.division || "ALL",
        status: form.status || "ACTIVE",
        notes: form.notes.trim() || null,
      };

      const result = editingId
        ? await supabase.from("service_cost_catalog").update(payload).eq("id", editingId)
        : await supabase.from("service_cost_catalog").insert({
            ...payload,
            sort_order: rows.length + 1,
          });
      if (result.error) throw result.error;
      resetForm();
      await fetchRows();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Hapus master biaya "${row.name}"?`)) return;
    setError("");
    try {
      const { error: deleteError } = await supabase
        .from("service_cost_catalog")
        .delete()
        .eq("id", row.id);
      if (deleteError) throw deleteError;
      if (editingId === row.id) resetForm();
      await fetchRows();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  return (
    <div className="page-stack">
      {formOpen ? <article className="panel master-biaya-panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">Sistem biaya</span>
            <h3>{editingId ? "Edit Master Biaya" : "Tambah Master Biaya"}</h3>
          </div>
        </div>

        {error ? <div className="alert alert-danger">{error}</div> : null}

        <form className="mb-form" onSubmit={handleSubmit}>
          <div className="mb-field mb-name">
            <label>Nama Pos Biaya *</label>
            <input
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Contoh: Sewa TDP"
            />
          </div>
          <div className="mb-field">
            <label>Tipe</label>
            <select value={form.cost_type} onChange={(e) => setField("cost_type", e.target.value)}>
              {costTypes.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <div className="mb-field">
            <label>Nominal Default</label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setField("amount", e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="mb-field">
            <label>Divisi</label>
            <select value={form.division} onChange={(e) => setField("division", e.target.value)}>
              {divisions.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <div className="mb-field">
            <label>Status</label>
            <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
              <option value="ACTIVE">Aktif</option>
              <option value="INACTIVE">Nonaktif</option>
            </select>
          </div>
          <div className="mb-field mb-note">
            <label>Catatan</label>
            <input
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Opsional"
            />
          </div>
          <div className="mb-actions">
            <button className="button button-primary" disabled={saving}>
              {saving ? "Menyimpan..." : editingId ? "Perbarui" : "Simpan"}
            </button>
            {editingId ? (
              <button type="button" className="button button-quiet" onClick={resetForm}>
                Batal Edit
              </button>
            ) : null}
          </div>
        </form>
      </article> : null}

      <article className="panel master-biaya-panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">Kamus biaya</span>
            <h3>Daftar Master Biaya</h3>
          </div>
          <div className="mb-toolbar">
            <input
              className="mb-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari pos biaya..."
            />
            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                setError("");
                setEditingId(null);
                setForm(emptyForm);
                setFormOpen(true);
              }}
            >
              <Plus size={16} /> Biaya
            </button>
          </div>
        </div>

        {error && !formOpen ? <div className="alert alert-danger">{error}</div> : null}

        {loading ? (
          <div className="content-loading"><div className="loader" /><span>Memuat master biaya...</span></div>
        ) : (
          <table className="mb-table">
            <thead>
              <tr>
                <th>Pos Biaya</th>
                <th>Divisi</th>
                <th>Tipe</th>
                <th>Nominal</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                    {row.notes ? <small>{row.notes}</small> : null}
                  </td>
                  <td><span className="status status-neutral">{divisionLabel(row.division)}</span></td>
                  <td>{costTypeLabel(row.cost_type)}</td>
                  <td>{formatAmount(row)}</td>
                  <td>
                    <span className={`status ${row.status === "ACTIVE" ? "status-positive" : "status-danger"}`}>
                      {row.status === "ACTIVE" ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  <td>
                    <button className="link-button" onClick={() => startEdit(row)}>Edit</button>
                    <button className="link-button link-danger" onClick={() => handleDelete(row)}>Hapus</button>
                  </td>
                </tr>
              ))}
              {!filteredRows.length ? (
                <tr>
                  <td colSpan={6} className="muted">Belum ada master biaya.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </article>

      <style>{`
        .master-biaya-panel {
          padding: 22px;
        }
        .mb-form {
          display: grid;
          grid-template-columns: 1.3fr 0.9fr 0.8fr 0.9fr 0.8fr;
          gap: 14px;
          align-items: end;
        }
        .mb-field {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }
        .mb-field label {
          font-size: 12px;
          font-weight: 700;
          color: #6b6555;
        }
        .mb-field input,
        .mb-field select,
        .mb-search {
          padding: 10px 12px;
          border: 1px solid #e8e2d5;
          border-radius: 8px;
          font-size: 14px;
          background: #fff;
        }
        .mb-note {
          grid-column: span 2;
        }
        .mb-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .mb-search {
          min-width: 260px;
        }
        .mb-toolbar {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .mb-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .mb-table th {
          text-align: left;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #7a7364;
          padding: 10px 12px;
          background: #f8f4ec;
          border-bottom: 1px solid #e8e2d5;
        }
        .mb-table td {
          padding: 12px;
          border-bottom: 1px solid #f1ece0;
          vertical-align: top;
        }
        .mb-table td strong,
        .mb-table td small {
          display: block;
        }
        .mb-table td small {
          margin-top: 3px;
          color: #6b7280;
        }
        .link-button {
          background: none;
          border: none;
          padding: 0;
          margin-right: 10px;
          color: #2f6f4f;
          font-weight: 700;
          cursor: pointer;
        }
        .link-danger {
          color: #c0392b;
        }
        @media (max-width: 980px) {
          .mb-form {
            grid-template-columns: 1fr 1fr;
          }
          .mb-note {
            grid-column: span 2;
          }
        }
        @media (max-width: 680px) {
          .mb-form {
            grid-template-columns: 1fr;
          }
          .mb-note {
            grid-column: auto;
          }
          .mb-actions {
            flex-direction: column;
            align-items: stretch;
          }
          .mb-search {
            min-width: 0;
            width: 100%;
          }
          .mb-toolbar {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}
